// ResultPage.jsx
// React equivalent of escape_peace-old/thankyou.php — final result screen.
// Phase 3/4: final score/time were only ever passed via React Router
// navigation state from GamePage, so a refresh lost them. Phase 5 adds a
// backend fallback (escapeGameResult) so the result survives a refresh or
// direct link, while still preferring the just-completed local state for
// an instant, no-flicker display right after finishing the game.
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EscapeHeader from '../../../components/escape-to-inner-peace/layout/EscapeHeader';
import { formatElapsedTime } from '../../../games/escape-to-inner-peace/utils/formatTime';
import { MAX_TOTAL_SCORE } from '../../../games/escape-to-inner-peace/hooks/useEscapeScore';
import { selectAdminToken } from '../../../admin/sessionSlice';
import { readUserId } from '../../../games/escape-to-inner-peace/hooks/useEscapePersistence';
import { fetchEscapeResult } from '../../../games/escape-to-inner-peace/services/escapeApi';
import thankyouGif from '../../../assets/escape-to-inner-peace/gifs/thankyou-gif.gif';
import '../../../games/escape-to-inner-peace/styles/theme.css';
import './result.css';

function ResultPage() {
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const localState = location.state;
  const hasLocalState = typeof localState?.score === 'number' && typeof localState?.seconds === 'number';

  const [status, setStatus] = useState(hasLocalState ? 'ready' : 'loading');
  const [result, setResult] = useState(
    hasLocalState ? { totalScore: localState.score, elapsedSeconds: localState.seconds } : null
  );

  useEffect(() => {
    if (hasLocalState) return undefined;

    let cancelled = false;
    const userId = readUserId();

    async function load() {
      if (!adminToken || !userId) {
        if (!cancelled) setStatus('invalid');
        return;
      }
      setStatus('loading');
      try {
        const res = await fetchEscapeResult({ token: adminToken, userId });
        if (cancelled) return;
        if (res?.result) {
          setResult({ totalScore: res.result.totalScore, elapsedSeconds: res.result.elapsedSeconds });
          setStatus('ready');
        } else {
          setStatus('invalid');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [hasLocalState, adminToken]);

  if (status === 'invalid') {
    return <Navigate to="/escape-to-inner-peace" replace />;
  }

  if (status === 'loading') {
    return (
      <div className="escape-result-page">
        <EscapeHeader backHref="https://extramileplay.com" />
        <p className="escape-result-status" data-testid="escape-result-loading">
          Loading your result…
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="escape-result-page">
        <EscapeHeader backHref="https://extramileplay.com" />
        <div className="escape-result-status" data-testid="escape-result-error">
          <p>We couldn&rsquo;t load your result. Please check your connection and try again.</p>
          <button
            type="button"
            className="escape-begin-btn"
            onClick={() => window.location.reload()}
            data-testid="escape-result-retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { totalScore, elapsedSeconds } = result;
  const timeLabel = formatElapsedTime(elapsedSeconds);

  return (
    <div className="escape-result-page">
      <EscapeHeader backHref="https://extramileplay.com" />

      <div className="escape-result-content">
        <img src={thankyouGif} alt="" className="escape-result-gif" />

        <div className="escape-result-score1" data-testid="escape-result-score">
          {`Your Score is ${totalScore} out of ${MAX_TOTAL_SCORE} in ${timeLabel} minutes.`}
        </div>
        {/* thankyou.php has a literal <br> here — the only gap in the whole
            block. Everything after it (heading + all 3 paragraphs) has no
            margin between pieces, so it reads as one continuous panel. */}
        <br />

        <div className="escape-result-score1">
          You&rsquo;ve Unlocked the Path to Self-Love &ndash; Now What&rsquo;s Next?
        </div>
        <div className="escape-result-score">
          Self-care, like the escape room, is a journey that needs daily effort. Shift your perspective to break free
          from stress.
        </div>
        <div className="escape-result-score">
          Make mindful choices by asking, &ldquo;Is this helping me?&rdquo; Balance your energy - give, but don&rsquo;t
          forget to recharge. Small actions matter - one breath, one pause, one kind thought can change everything.
        </div>
        <div className="escape-result-score">
          Commit to one self-care action today. Choosing yourself makes you stronger every day.
        </div>
      </div>
    </div>
  );
}

export default ResultPage;
